import { nextTick, Ref, ComputedRef } from 'vue';
import { cloneDeep } from 'lodash-es';
import { TOKEN_STATUS } from '@/common/components/molecules/filterSearch/filterSearch.define';
import {
  EditingFilterToken,
  FilterDropdownInfo,
  FilterDropdownItem,
  FilterItem,
  FilterSelectedValues,
  FilterToken,
  FinishedFilterToken,
} from '@/common/components/molecules/filterSearch/filterSearch.type';

interface UseFilterTokensParams {
  filterItems: Ref<FilterItem[]>;
  tokenList: Ref<FilterToken[]>;
  editingToken: Ref<EditingFilterToken | null>;
  lastToken: ComputedRef<FilterToken>;
  selectedLogicalOperator: Ref<string>;
  dropdownListInfo: FilterDropdownInfo;
  tokenRefs: Ref<HTMLElement[]>;
  moreCount: Ref<number>;
  setKeyDropdownList: () => void;
  setOperatorDropdownList: (keyId: string) => void;
  setValueDropdownList: (
    keyId: string,
    filterFn?: (item: FilterDropdownItem) => boolean,
    sortFn?: (a: FilterDropdownItem, b: FilterDropdownItem) => number,
  ) => void;
  setMultiSelectedValues: (
    targetToken: FilterToken,
    selectedDropdownItem: FilterDropdownItem,
  ) => void;
  isSelectedDropdownItem: (item: FilterDropdownItem) => boolean;
  initHoveredDropdownItemIndex: () => void;
  showDropdownList: () => void;
  hideDropdownList: () => void;
  focusPartsInput: (targetTokenID: number) => void;
  setMoreCount: () => Promise<void>;
  saveHistory: (targetToken: FilterToken) => Promise<void>;
  getEmitComplete: () => () => void;
}

export function useFilterTokens({
  filterItems,
  tokenList,
  editingToken,
  lastToken,
  selectedLogicalOperator,
  dropdownListInfo,
  tokenRefs,
  moreCount,
  setKeyDropdownList,
  setOperatorDropdownList,
  setValueDropdownList,
  setMultiSelectedValues,
  isSelectedDropdownItem,
  initHoveredDropdownItemIndex,
  showDropdownList,
  hideDropdownList,
  focusPartsInput,
  setMoreCount,
  saveHistory,
  getEmitComplete,
}: UseFilterTokensParams) {
  const convertToFilterSelectedValues = (tokens: FilterToken[]): FilterSelectedValues => {
    const finishedTokenList = tokens.filter(
      ({ status }) => status === TOKEN_STATUS.VALUE_DONE,
    ) as FinishedFilterToken[];

    return {
      logicalOperator: selectedLogicalOperator.value,
      tokens: finishedTokenList?.map(({ key, operator, values }) => ({
        key: key.id,
        operator: operator.id,
        value: values?.map(({ id }) => id).join(','),
      })),
    };
  };

  const removeTokens = () => {
    tokenList.value = [];
    tokenRefs.value = [];
    moreCount.value = 0;
  };

  const removeToken = async (targetTokenIndex: number) => {
    if (targetTokenIndex < 0) {
      return;
    }

    tokenList.value.splice(targetTokenIndex, 1);
    tokenRefs.value.splice(targetTokenIndex, 1);

    if (!tokenList.value?.length) {
      setKeyDropdownList();
    }

    await setMoreCount();
    getEmitComplete()();
  };

  const isActiveAddMultiValueBtn = (targetToken: FilterToken): boolean => {
    const filterItem = filterItems.value.find((item) => item.key.id === targetToken.key.id);
    const selectableItemCount = filterItem?.values?.items?.length;
    const targetTokenValuesCount = targetToken.values?.length;
    const isAllSelected = selectableItemCount === targetTokenValuesCount;

    return (
      !!filterItem?.values?.multi &&
      targetToken.operator?.id !== 'like' &&
      targetToken.operator?.id !== 'not like' &&
      !isAllSelected
    );
  };

  const getValueWithDefaultUnit = (
    valueData: FilterDropdownItem,
    filterKey: string,
  ): FilterDropdownItem => {
    const filterItem = filterItems.value.find((item) => item.key.id === filterKey);
    const defaultUnit = filterItem?.defaultUnit ?? '';

    if (!/^-?\d{1,3}(,\d{3})*(\.\d+)?$|^-?\d+(\.\d+)?$/.test(valueData.id)) {
      return valueData;
    }

    const valueWithUnit = `${valueData.id}${defaultUnit}`;

    return {
      id: valueWithUnit,
      name: valueWithUnit,
    };
  };

  const addTokenParts = (selectedDropdownItem: FilterDropdownItem) => {
    const { multi, category, items } = dropdownListInfo;

    if (!multi || !items?.length) {
      initHoveredDropdownItemIndex();
      mainInputValueReset();
    }

    switch (category) {
      case 'key': {
        tokenList.value.push({
          id: Date.now(),
          key: selectedDropdownItem,
          operator: undefined,
          values: [],
          status: TOKEN_STATUS.OPERATOR_ING,
        });
        setOperatorDropdownList(selectedDropdownItem.id);
        break;
      }

      case 'operator': {
        lastToken.value.operator = selectedDropdownItem;
        lastToken.value.status = TOKEN_STATUS.OPERATOR_DONE;
        setValueDropdownList(
          lastToken.value.key.id,
          () => selectedDropdownItem?.id !== 'like' && lastToken.value.operator?.id !== 'not like',
        );
        break;
      }

      case 'value': {
        if (multi && items?.length) {
          setMultiSelectedValues(lastToken.value, selectedDropdownItem);
          setValueDropdownList(
            lastToken.value.key.id,
            () => lastToken.value.operator?.id !== 'like',
          );
          lastToken.value.status = TOKEN_STATUS.VALUE_ING;
        } else {
          lastToken.value.values = [
            getValueWithDefaultUnit(selectedDropdownItem, lastToken.value.key.id),
          ];
          lastToken.value.status = TOKEN_STATUS.VALUE_DONE;
          setKeyDropdownList();
        }
        break;
      }

      default:
        break;
    }
  };

  // mainInputValue는 layout에서 관리하지만, addTokenParts에서 리셋이 필요
  // 외부에서 mainInputValue를 넘겨받는 대신 콜백으로 처리
  let mainInputValueReset = () => {};
  const setMainInputValueReset = (fn: () => void) => {
    mainInputValueReset = fn;
  };

  const updateTargetTokenStatusByEditingToken = () => {
    const targetToken = tokenList.value.find((token) => token.id === editingToken.value?.id);
    if (!targetToken) {
      return;
    }

    if (targetToken.key && !targetToken.operator && !targetToken.values.length) {
      targetToken.status = TOKEN_STATUS.OPERATOR_ING;
    } else if (targetToken.key && targetToken.operator && !targetToken.values.length) {
      targetToken.status = TOKEN_STATUS.OPERATOR_DONE;
    } else if (targetToken.key && targetToken.operator && targetToken.values.length) {
      targetToken.status = TOKEN_STATUS.VALUE_DONE;
    }

    if (editingToken.value) {
      editingToken.value.status = targetToken.status;
    }
  };

  const quitEditingToken = async () => {
    if (editingToken.value) {
      const targetToken = tokenList.value.find((t) => t.id === editingToken.value?.id);

      if (!targetToken) {
        editingToken.value = null;
        dropdownListInfo.isEditing = false;
        return;
      }

      if (
        targetToken.status === TOKEN_STATUS.KEY_ING ||
        targetToken.status === TOKEN_STATUS.OPERATOR_ING ||
        !targetToken.key?.id
      ) {
        const index = tokenList.value.indexOf(targetToken);
        if (index > -1) {
          tokenList.value.splice(index, 1);
          tokenRefs.value.splice(index, 1);
        }
      } else {
        updateTargetTokenStatusByEditingToken();

        if (editingToken.value.status === TOKEN_STATUS.VALUE_DONE) {
          await saveHistory(editingToken.value);
          await setMoreCount();
        }
      }

      editingToken.value = null;
      dropdownListInfo.isEditing = false;
    }
  };

  const editTokenParts = async (selectedDropdownItem: FilterDropdownItem | null) => {
    if (!editingToken.value) {
      return;
    }

    const targetToken = tokenList.value.find((token) => token.id === editingToken.value?.id);
    if (!targetToken) {
      return;
    }

    const { multi, category, items } = dropdownListInfo;
    switch (category) {
      case 'key': {
        if (!selectedDropdownItem) {
          return;
        }

        editingToken.value.key = selectedDropdownItem;
        targetToken.key = selectedDropdownItem;
        targetToken.status = TOKEN_STATUS.OPERATOR_ING;
        editingToken.value.status = TOKEN_STATUS.OPERATOR_ING;
        setOperatorDropdownList(selectedDropdownItem.id);
        await nextTick();
        focusPartsInput(editingToken.value.id);
        break;
      }

      case 'operator': {
        if (!selectedDropdownItem) {
          return;
        }

        editingToken.value.operator = selectedDropdownItem;
        targetToken.operator = selectedDropdownItem;

        const isCreationMode = editingToken.value.status !== TOKEN_STATUS.VALUE_DONE;
        if (isCreationMode) {
          targetToken.status = TOKEN_STATUS.OPERATOR_DONE;
          editingToken.value.status = TOKEN_STATUS.OPERATOR_DONE;
          setValueDropdownList(
            targetToken.key.id,
            () => selectedDropdownItem?.id !== 'like' && selectedDropdownItem?.id !== 'not like',
          );
          await nextTick();
          focusPartsInput(editingToken.value.id);
        } else {
          hideDropdownList();
          await quitEditingToken();
        }
        break;
      }

      case 'value': {
        if (!selectedDropdownItem) {
          return;
        }

        const isMultiDropdown = selectedDropdownItem && multi && items?.length;
        if (isMultiDropdown && selectedDropdownItem) {
          setMultiSelectedValues(targetToken, selectedDropdownItem);
        } else {
          if (isMultiDropdown && !selectedDropdownItem) {
            editingToken.value.values = [];
            targetToken.values = [];
            targetToken.status = TOKEN_STATUS.OPERATOR_DONE;
          } else if (selectedDropdownItem) {
            const valueData = getValueWithDefaultUnit(
              selectedDropdownItem,
              editingToken.value.key.id,
            );
            editingToken.value.values = [valueData];
            targetToken.values = [valueData];
            targetToken.status = TOKEN_STATUS.VALUE_DONE;
          }
          hideDropdownList();
          await quitEditingToken();
        }
        break;
      }

      default:
        break;
    }
  };

  const createDraftToken = async () => {
    if (editingToken.value?.status === TOKEN_STATUS.KEY_ING) {
      await nextTick();
      focusPartsInput(editingToken.value.id);
      return;
    }

    const draftToken: FilterToken = {
      id: Date.now(),
      key: { id: '', name: '' },
      operator: undefined,
      values: [],
      status: TOKEN_STATUS.KEY_ING,
    };
    tokenList.value.push(draftToken);
    editingToken.value = {
      ...draftToken,
      backupValues: [],
    };
    dropdownListInfo.isEditing = true;
    setKeyDropdownList();
    showDropdownList();
    await nextTick();
    focusPartsInput(draftToken.id);
  };

  const setTokenParts = async (selectedDropdownItem: FilterDropdownItem | null) => {
    if (editingToken.value) {
      await editTokenParts(selectedDropdownItem);
    } else if (selectedDropdownItem && !editingToken.value) {
      addTokenParts(selectedDropdownItem);
    }

    await setMoreCount();
  };

  const onClickResumeToken = async (targetToken: FilterToken) => {
    if (editingToken.value) {
      await quitEditingToken();
    }

    editingToken.value = {
      ...targetToken,
      backupValues: cloneDeep(targetToken.values),
    };
    dropdownListInfo.isEditing = true;

    if (
      targetToken.status === TOKEN_STATUS.KEY_ING ||
      targetToken.status === TOKEN_STATUS.KEY_DONE
    ) {
      if (!targetToken.key?.id) {
        setKeyDropdownList();
      } else {
        setOperatorDropdownList(targetToken.key.id);
      }
    } else if (targetToken.status === TOKEN_STATUS.OPERATOR_ING) {
      setOperatorDropdownList(targetToken.key.id);
    } else if (targetToken.status === TOKEN_STATUS.OPERATOR_DONE) {
      setValueDropdownList(
        targetToken.key.id,
        () => targetToken.operator?.id !== 'like' && targetToken.operator?.id !== 'not like',
      );
    }

    showDropdownList();
    await nextTick();
    focusPartsInput(targetToken.id);
  };

  const onClickAddMultiValue = async (targetToken: FilterToken) => {
    if (editingToken.value) {
      await quitEditingToken();
    }

    editingToken.value = {
      ...targetToken,
      backupValues: cloneDeep(targetToken.values),
    };

    setValueDropdownList(targetToken.key.id, (item) =>
      targetToken.operator?.id === 'like' || targetToken.operator?.id === 'not like'
        ? false
        : !isSelectedDropdownItem(item),
    );

    dropdownListInfo.isEditing = true;
    showDropdownList();

    await nextTick();
    focusPartsInput(targetToken.id);
  };

  const setTokenListBySelectedResult = (selectedValues: FilterSelectedValues | undefined) => {
    if (!selectedValues) {
      return;
    }

    const { logicalOperator, tokens } = selectedValues;
    selectedLogicalOperator.value = logicalOperator;

    if (!tokens?.length) {
      return;
    }

    tokens?.forEach(({ key, operator, value }, index) => {
      const filterItem = filterItems.value.find((item) => item.key.id === key);

      const { multi, items } = filterItem?.values ?? {};
      let values: FilterDropdownItem[];
      if (items?.length && multi && operator !== 'like' && operator !== 'not like') {
        values = items.filter((item) => value.split(',').includes(item.id));
      } else {
        values = [{ id: value, name: value }];
      }

      if (filterItem) {
        tokenList.value.push({
          id: +`${Date.now()}${index}`,
          key: filterItem.key,
          operator: {
            id: operator,
            name: operator,
          },
          values,
          status: TOKEN_STATUS.VALUE_DONE,
        });
      }
    });

    getEmitComplete()();
  };

  return {
    convertToFilterSelectedValues,
    removeTokens,
    removeToken,
    isActiveAddMultiValueBtn,
    getValueWithDefaultUnit,
    addTokenParts,
    editTokenParts,
    createDraftToken,
    setTokenParts,
    quitEditingToken,
    updateTargetTokenStatusByEditingToken,
    onClickResumeToken,
    onClickAddMultiValue,
    setTokenListBySelectedResult,
    setMainInputValueReset,
  };
}
